import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, getComicsByCategory } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const filteredComics = useMemo(() => {
    let result = getComicsByCategory(activeSlug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    return result;
  }, [activeSlug, sortBy]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部'} - 韩漫之家`,
    description: `韩漫之家${currentCategory?.name || '全部'}韩国漫画分类，海量韩国漫画免费在线阅读`,
    url: `https://hanmanzhijia.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'https://hanmanzhijia.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory?.name || '全部漫画' },
              ]}
            />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
              {currentCategory?.name || '全部'} <strong className="text-primary-500">韩国漫画</strong>
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              共 {currentCategory?.count || filteredComics.length} 部作品
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-48 flex-shrink-0">
              <nav className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" aria-label="漫画分类">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                    <span className={`ml-1.5 text-xs ${
                      activeSlug === cat.slug ? 'text-background-300' : 'text-foreground-400'
                    }`}>
                      {cat.count}
                    </span>
                  </Link>
                ))}
              </nav>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm text-foreground-500">
                  共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品
                </span>
                <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
                  {([
                    { key: 'latest' as const, label: '最新' },
                    { key: 'popular' as const, label: '最热' },
                    { key: 'rating' as const, label: '评分' },
                  ]).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? 'bg-background-50 text-primary-500 shadow-sm'
                          : 'text-foreground-500 hover:text-foreground-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}